import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowUpDown, ChevronDown, ChevronUp, Columns3, ExternalLink, MonitorPlay, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SearchInput } from '@/components/ui/search-input';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { PageHeader } from '@/components/PageHeader';
import { StatusBadge } from '@/components/StatusBadge';
import { StringMultiSelect } from '@/components/StringMultiSelect';
import { DesktopOnly } from '@/components/dashboard/DesktopOnly';
import { AlertCountBadge, AvBadge, DeviceStatusBadge, PatchBadge } from '@/components/rmm/DeviceBadges';
import { useRmmDevices, type RmmDevice } from '@/api/rmm';
import { useRemoteCoverage, type RemoteDeviceCoverage, type RemoteState } from '@/api/screenconnect';
import { fmtDate, fmtRelative } from '@/lib/cobalt-helpers';
import {
  AV_STATUS_OPTIONS,
  PATCH_STATUS_OPTIONS,
  avStatusMeta,
  daysSince,
  deviceClientName,
  patchStatusMeta,
} from '@/lib/rmm-helpers';
import { rmmDevicePath, rmmSitePath } from '@/lib/routes';
import { cn } from '@/lib/utils';
import { useScopeFilter } from '@/contexts/ClientScopeContext';

// ---- Column definitions ----

type ColumnKey =
  | 'hostname' | 'client' | 'site' | 'category' | 'status' | 'lastUser'
  | 'patch' | 'av' | 'reboot' | 'alerts' | 'remote'
  | 'os' | 'intIp' | 'extIp' | 'agent' | 'warranty'
  | 'manufacturer' | 'model' | 'serial'
  | 'links';
type SortKey = Exclude<ColumnKey, 'links'>;
type SortDir = 'asc' | 'desc';
type SortState = { key: SortKey; dir: SortDir } | null;

interface ColumnDef {
  key: ColumnKey;
  label: string;
  group: 'Core' | 'Health' | 'System' | 'Hardware' | '';
  defaultWidth: number;
  minWidth: number;
  required?: boolean;
  unsortable?: boolean;
}

const ALL_COLUMNS: ColumnDef[] = [
  { key: 'hostname',     label: 'Hostname',     group: 'Core',     defaultWidth: 180, minWidth: 100, required: true },
  { key: 'client',       label: 'Client',       group: 'Core',     defaultWidth: 190, minWidth: 80 },
  { key: 'site',         label: 'Site',         group: 'Core',     defaultWidth: 120, minWidth: 70 },
  { key: 'category',     label: 'Type',         group: 'Core',     defaultWidth: 145, minWidth: 70 },
  { key: 'status',       label: 'Status',       group: 'Core',     defaultWidth: 135, minWidth: 110 },
  { key: 'lastUser',     label: 'Last User',    group: 'Core',     defaultWidth: 150, minWidth: 80 },
  { key: 'patch',        label: 'Patching',     group: 'Health',   defaultWidth: 140, minWidth: 90 },
  { key: 'av',           label: 'Antivirus',    group: 'Health',   defaultWidth: 120, minWidth: 80 },
  { key: 'reboot',       label: 'Reboot',       group: 'Health',   defaultWidth: 80,  minWidth: 60 },
  { key: 'alerts',       label: 'Alerts',       group: 'Health',   defaultWidth: 75,  minWidth: 60 },
  { key: 'remote',       label: 'ScreenConnect', group: 'Health',  defaultWidth: 125, minWidth: 90 },
  { key: 'os',           label: 'OS',           group: 'System',   defaultWidth: 220, minWidth: 80 },
  { key: 'intIp',        label: 'Internal IP',  group: 'System',   defaultWidth: 120, minWidth: 80 },
  { key: 'extIp',        label: 'External IP',  group: 'System',   defaultWidth: 120, minWidth: 80 },
  { key: 'agent',        label: 'Agent',        group: 'System',   defaultWidth: 110, minWidth: 70 },
  { key: 'warranty',     label: 'Warranty',     group: 'System',   defaultWidth: 110, minWidth: 70 },
  { key: 'manufacturer', label: 'Manufacturer', group: 'Hardware', defaultWidth: 120, minWidth: 70 },
  { key: 'model',        label: 'Model',        group: 'Hardware', defaultWidth: 200, minWidth: 80 },
  { key: 'serial',       label: 'Serial',       group: 'Hardware', defaultWidth: 130, minWidth: 80 },
  { key: 'links',        label: '',             group: '',         defaultWidth: 64,  minWidth: 64,  required: true, unsortable: true },
];

const COL_BY_KEY = new Map(ALL_COLUMNS.map((c) => [c.key, c]));
const DEFAULT_VISIBLE: ColumnKey[] = ALL_COLUMNS.map((c) => c.key);
/** Columns added after people saved their column choice: shown once, then the saved choice rules (added 2026-10-21). */
const NEW_COLUMNS: ColumnKey[] = ['remote'];

// ---- Filters ----

/** Combined online/last-seen filter — "offline N+" means not seen for at least N days. */
type StatusFilter = 'any' | 'online' | 'offline' | 'offline7' | 'offline30' | 'offline90';
const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'any',       label: 'Any status' },
  { value: 'online',    label: 'Online' },
  { value: 'offline',   label: 'Offline' },
  { value: 'offline7',  label: 'Not seen 7+ days' },
  { value: 'offline30', label: 'Not seen 30+ days' },
  { value: 'offline90', label: 'Not seen 90+ days' },
];

interface DeviceFilters {
  search: string;
  clients: string[];
  categories: string[];
  status: StatusFilter;
  patch: string[];
  av: string[];
  rebootRequired: boolean;
  hasAlerts: boolean;
  unlinked: boolean;
  /** Only computers that should have a ScreenConnect session but don't (the `no-remote` gap). */
  noRemote: boolean;
}

const DEFAULT_FILTERS: DeviceFilters = {
  search: '', clients: [], categories: [], status: 'any', patch: [], av: [],
  rebootRequired: false, hasAlerts: false, unlinked: false, noRemote: false,
};

function matchesStatus(d: RmmDevice, f: StatusFilter): boolean {
  if (f === 'any') return true;
  if (f === 'online') return d.online;
  if (d.online) return false;
  const min = f === 'offline' ? 0 : Number(f.slice('offline'.length));
  return (daysSince(d.lastSeen) ?? Infinity) >= min;
}

/**
 * Pre-filtered views linked from the sidebar Views and dashboards (`rmmDevicesView` in
 * routes.ts): `status=offline30`, `reboot=1`, `alerts=1`, `remote=missing`, `patch=A,B`, `av=A,B`,
 * `client=<name>`, `type=<category>`, `q=<search>`. Returns null when the URL carries none.
 */
function filtersFromSearch(sp: URLSearchParams): Partial<DeviceFilters> | null {
  const f: Partial<DeviceFilters> = {};
  const status = sp.get('status');
  if (status && STATUS_OPTIONS.some((o) => o.value === status)) f.status = status as StatusFilter;
  if (sp.get('reboot') === '1') f.rebootRequired = true;
  if (sp.get('alerts') === '1') f.hasAlerts = true;
  if (sp.get('remote') === 'missing') f.noRemote = true;
  const list = (k: string) => sp.get(k)?.split(',').filter(Boolean);
  if (list('patch')?.length) f.patch = list('patch');
  if (list('av')?.length) f.av = list('av');
  if (sp.get('client')) f.clients = [sp.get('client')!];
  if (sp.get('type')) f.categories = [sp.get('type')!];
  if (sp.get('q')) f.search = sp.get('q')!;
  return Object.keys(f).length ? f : null;
}

// ---- Persistence ----

const STORAGE_KEY = 'cosmos:rmm:devices';

function load<T>(key: string, fallback: T): T {
  try { const r = localStorage.getItem(key); return r ? JSON.parse(r) as T : fallback; } catch { return fallback; }
}
function save(key: string, value: unknown) {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* storage unavailable */ }
}

// ---- Page ----

export default function RmmDevicesPage() {
  const [filters, setFilters] = useState<DeviceFilters>(() => ({ ...DEFAULT_FILTERS, ...load<Partial<DeviceFilters>>(STORAGE_KEY, {}) }));
  const [sort, setSort]       = useState<SortState>(() => load<SortState>(`${STORAGE_KEY}:sort`, { key: 'client', dir: 'asc' }));
  const [widths, setWidths]   = useState<Partial<Record<ColumnKey, number>>>(() => load(`${STORAGE_KEY}:widths`, {}));
  const [visible, setVisible] = useState<Set<ColumnKey>>(() => {
    const saved = new Set(load<ColumnKey[]>(`${STORAGE_KEY}:visible`, DEFAULT_VISIBLE));
    const seen = new Set(load<ColumnKey[]>(`${STORAGE_KEY}:seenColumns`, []));
    for (const k of NEW_COLUMNS) if (!seen.has(k)) saved.add(k);
    save(`${STORAGE_KEY}:seenColumns`, ALL_COLUMNS.map((c) => c.key));
    return saved;
  });

  // A menu link's view replaces the saved filters, then leaves the URL so it doesn't stick.
  // Runs on arrival and when a menu link is picked while already on this page.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const view = filtersFromSearch(searchParams);
    if (!view) return;
    setFilters({ ...DEFAULT_FILTERS, ...view });
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => save(STORAGE_KEY, filters), [filters]);
  useEffect(() => save(`${STORAGE_KEY}:sort`, sort), [sort]);
  useEffect(() => save(`${STORAGE_KEY}:widths`, widths), [widths]);
  useEffect(() => save(`${STORAGE_KEY}:visible`, Array.from(visible)), [visible]);

  const devices = useRmmDevices({ scoped: true });
  const remoteQ = useRemoteCoverage();
  const remote = remoteQ.data?.configured ? remoteQ.data : null;
  const remoteOf = useCallback((d: RmmDevice) => remote?.items[d.uid], [remote]);
  // While a client is picked in the header, this page's own Client filter is ignored (and hidden).
  const { companyId: scopeId } = useScopeFilter();
  const clients = scopeId != null ? [] : filters.clients;
  const items = useMemo(() => devices.data?.items ?? [], [devices.data]);

  const clientOpts = useMemo(
    () => [...new Set(items.map(deviceClientName))].sort((a, b) => a.localeCompare(b)).map((v) => ({ value: v, label: v })),
    [items],
  );
  const categoryOpts = useMemo(
    () => [...new Set(items.map((d) => d.category ?? 'Unknown'))].sort().map((v) => ({ value: v, label: v })),
    [items],
  );

  const searchQuery = filters.search.trim().toLowerCase();
  const filtered = useMemo(() => items.filter((d) => {
    if (clients.length && !clients.includes(deviceClientName(d))) return false;
    if (filters.categories.length && !filters.categories.includes(d.category ?? 'Unknown')) return false;
    if (!matchesStatus(d, filters.status)) return false;
    if (filters.patch.length && !filters.patch.includes(d.patchStatus ?? '')) return false;
    if (filters.av.length && !filters.av.includes(d.antivirusStatus ?? '')) return false;
    if (filters.rebootRequired && !d.rebootRequired) return false;
    if (filters.hasAlerts && d.openAlerts === 0) return false;
    if (filters.unlinked && d.autotaskCompanyId != null) return false;
    if (filters.noRemote && remoteOf(d)?.state !== 'missing') return false;
    if (searchQuery) {
      const hay = [
        d.hostname, d.description, d.lastLoggedInUser, d.intIpAddress, d.extIpAddress,
        deviceClientName(d), d.siteName, d.operatingSystem,
        d.hardware?.serialNumber, d.hardware?.model, d.hardware?.manufacturer,
      ];
      if (!hay.some((v) => v?.toLowerCase().includes(searchQuery))) return false;
    }
    return true;
  }), [items, filters, searchQuery, clients, remoteOf]);

  const sorted = useMemo(() => {
    if (!sort) return filtered;
    const dir = sort.dir === 'asc' ? 1 : -1;
    const get = (d: RmmDevice): string | number => {
      switch (sort.key) {
        case 'hostname':     return d.hostname.toLowerCase();
        case 'client':       return `${deviceClientName(d)}\u0000${d.hostname}`.toLowerCase();
        case 'site':         return d.siteName.toLowerCase();
        case 'category':     return (d.category ?? '').toLowerCase();
        // Online first, then most recently seen.
        case 'status':       return d.online ? Number.MAX_SAFE_INTEGER : d.lastSeen ? new Date(d.lastSeen).getTime() : 0;
        case 'lastUser':     return (d.lastLoggedInUser ?? '').toLowerCase();
        case 'patch':        return patchStatusMeta(d.patchStatus)?.label ?? '';
        case 'av':           return avStatusMeta(d.antivirusStatus)?.label ?? '';
        case 'reboot':       return d.rebootRequired ? 1 : 0;
        case 'alerts':       return d.openAlerts;
        // Gaps first, then sessions online / offline, then not expected.
        case 'remote':       return REMOTE_RANK[remoteOf(d)?.state ?? 'n/a'] + (remoteOf(d)?.online ? 0 : 0.5);
        case 'os':           return (d.operatingSystem ?? '').toLowerCase();
        case 'intIp':        return ipSortKey(d.intIpAddress);
        case 'extIp':        return ipSortKey(d.extIpAddress);
        case 'agent':        return d.agentVersion ?? '';
        case 'warranty':     return d.warrantyDate ?? '';
        case 'manufacturer': return (d.hardware?.manufacturer ?? '').toLowerCase();
        case 'model':        return (d.hardware?.model ?? '').toLowerCase();
        case 'serial':       return (d.hardware?.serialNumber ?? '').toLowerCase();
      }
    };
    return [...filtered].sort((a, b) => {
      const va = get(a), vb = get(b);
      return va < vb ? -dir : va > vb ? dir : 0;
    });
  }, [filtered, sort, remoteOf]);

  const visibleCols = useMemo(() => ALL_COLUMNS.filter((c) => c.required || visible.has(c.key)), [visible]);

  // ---- Column resize (same mechanics as the PSA grids) ----

  const dragRef = useRef<{ key: ColumnKey; startX: number; startWidth: number } | null>(null);
  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag) return;
      const min = COL_BY_KEY.get(drag.key)?.minWidth ?? 60;
      setWidths((w) => ({ ...w, [drag.key]: Math.max(min, drag.startWidth + (e.clientX - drag.startX)) }));
    };
    const onUp = () => { if (dragRef.current) document.body.style.cursor = ''; dragRef.current = null; };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => { window.removeEventListener('pointermove', onMove); window.removeEventListener('pointerup', onUp); };
  }, []);

  const startResize = useCallback((key: ColumnKey, e: React.PointerEvent) => {
    e.preventDefault(); e.stopPropagation();
    dragRef.current = { key, startX: e.clientX, startWidth: widths[key] ?? COL_BY_KEY.get(key)?.defaultWidth ?? 120 };
    document.body.style.cursor = 'col-resize';
  }, [widths]);

  function cycleSort(key: SortKey) {
    setSort((prev) => {
      if (!prev || prev.key !== key) return { key, dir: 'asc' };
      if (prev.dir === 'asc') return { key, dir: 'desc' };
      return null;
    });
  }

  const hw = devices.data?.hardware;
  const unlinkedSites = devices.data?.sites.filter((s) => s.autotaskCompanyId == null && s.deviceCount > 0) ?? [];

  return (
    <DesktopOnly
      title="Devices are available on desktop"
      message="Open Cosmos on a larger screen to browse RMM devices. The mobile app focuses on tickets and opportunities."
    >
      <div className="mx-auto w-full max-w-[1900px] px-4 py-4">
        <PageHeader
          title="Devices"
          count={devices.data ? filtered.length : null}
          isFetching={devices.isFetching}
          subtitle={
            <>
              Datto RMM · All clients
              {devices.data && <> · Updated {fmtRelative(devices.data.fetchedAt)}</>}
              {hw && hw.audited < hw.total && (
                <span title="Manufacturer, model and serial come from a per-device audit that runs in the background.">
                  {' '}· Hardware details {hw.audited}/{hw.total}
                </span>
              )}
              {remote && !remote.ready && (
                <span title="ScreenConnect machine details are read in the background; until then devices without a matched session show as 'checking', not as gaps.">
                  {' '}· ScreenConnect details {remote.details.read}/{remote.details.total}
                </span>
              )}
            </>
          }
          actions={
            <ColumnPicker
              visible={visible}
              onToggle={(k, on) => setVisible((prev) => { const next = new Set(prev); on ? next.add(k) : next.delete(k); return next; })}
              onReset={() => setVisible(new Set(DEFAULT_VISIBLE))}
              onResetWidths={() => setWidths({})}
            />
          }
        />

        <SummaryStrip items={items} remote={remote ? { ready: remote.ready, missing: items.filter((d) => remoteOf(d)?.state === 'missing').length } : null}
          onApply={(patch) => setFilters({ ...DEFAULT_FILTERS, ...patch })} />

        <div className="sticky top-0 z-10 -mx-4 mt-2 border-b bg-background/80 px-4 py-3 backdrop-blur">
          <FilterBar
            filters={filters}
            onChange={setFilters}
            clientOpts={clientOpts}
            categoryOpts={categoryOpts}
            unlinkedCount={unlinkedSites.length}
            remoteConfigured={!!remote}
          />
        </div>

        {devices.error ? (
          <div className="mt-6 rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
            Failed to load devices: {devices.error instanceof Error ? devices.error.message : 'Unknown error'}
          </div>
        ) : null}

        <div className="mt-4 overflow-x-auto rounded-md border">
          <table className="w-full text-sm" style={{ tableLayout: 'fixed' }}>
            <colgroup>
              {visibleCols.map((c) => <col key={c.key} style={{ width: widths[c.key] ?? c.defaultWidth }} />)}
            </colgroup>
            <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                {visibleCols.map((c) => (
                  <HeaderCell key={c.key} col={c} sort={sort} onSort={cycleSort} onResizeStart={startResize} />
                ))}
              </tr>
            </thead>
            <tbody className="divide-y">
              {devices.isLoading ? (
                <tr><td colSpan={visibleCols.length} className="px-3 py-12 text-center text-muted-foreground">Loading devices…</td></tr>
              ) : sorted.length === 0 ? (
                <tr><td colSpan={visibleCols.length} className="px-3 py-12 text-center text-muted-foreground">No devices match these filters.</td></tr>
              ) : null}
              {sorted.map((d) => (
                <tr key={d.uid} className="hover:bg-muted/30">
                  {visibleCols.map((c) => (
                    <td key={c.key} className="overflow-hidden px-3 py-2 align-top">{renderCell(c.key, d, remote ? remoteOf(d) ?? null : undefined)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </DesktopOnly>
  );
}

// ---- Cells ----

const dash = <span className="text-muted-foreground">—</span>;

function Truncate({ children, title }: { children: ReactNode; title?: string | null }) {
  return <div className="truncate" title={title ?? undefined}>{children}</div>;
}

const REMOTE_RANK: Record<RemoteState, number> = { missing: 0, unknown: 1, yes: 2, 'n/a': 3 };

/** `remote`: undefined = ScreenConnect not configured; null = no row for this device yet. */
function renderCell(key: ColumnKey, d: RmmDevice, remote?: RemoteDeviceCoverage | null): ReactNode {
  switch (key) {
    case 'remote': {
      if (remote === undefined) return <span className="text-xs text-muted-foreground" title="ScreenConnect isn't configured">—</span>;
      const s = remote?.state ?? 'n/a';
      if (s === 'missing') return <StatusBadge kind="warning">No session</StatusBadge>;
      if (s === 'unknown') return <span className="text-xs text-muted-foreground" title="ScreenConnect machine details are still loading">checking…</span>;
      if (s === 'n/a') return <span className="text-xs text-muted-foreground" title="Not expected: not a Windows/macOS computer, stale, or the client doesn't use ScreenConnect">—</span>;
      return <span className={cn('text-xs', remote?.online ? 'text-success' : 'text-muted-foreground')}>{remote?.online ? '● Online' : '○ Offline'}</span>;
    }
    case 'hostname':
      return (
        <Link to={rmmDevicePath(d.uid)} className="block truncate font-medium text-foreground hover:underline" title={d.description ?? d.hostname}>
          {d.hostname}
        </Link>
      );
    case 'client': {
      const name = deviceClientName(d);
      return d.autotaskCompanyId == null ? (
        <Truncate title={`Datto site "${d.siteName}" isn't linked to an Autotask company`}>
          {name} <span className="text-[11px] text-warning">· unlinked</span>
        </Truncate>
      ) : <Truncate title={name}>{name}</Truncate>;
    }
    case 'site':
      return (
        <Link to={rmmSitePath(d.siteUid)} className="block truncate text-xs leading-5 hover:underline" title={`All devices at ${d.siteName}`}>
          {d.siteName}
        </Link>
      );
    case 'category':
      return <Truncate title={d.type}>{d.category ?? dash}</Truncate>;
    case 'status':
      return <DeviceStatusBadge d={d} />;
    case 'lastUser':
      return d.lastLoggedInUser ? <Truncate title={d.lastLoggedInUser}>{stripDomain(d.lastLoggedInUser)}</Truncate> : dash;
    case 'patch':
      return <PatchBadge d={d} />;
    case 'av':
      return <AvBadge d={d} />;
    case 'reboot':
      return d.rebootRequired ? <StatusBadge kind="warning">Yes</StatusBadge> : dash;
    case 'alerts':
      return <AlertCountBadge d={d} />;
    case 'os':
      return <Truncate title={d.operatingSystem}><span className="text-xs">{d.operatingSystem ?? '—'}</span></Truncate>;
    case 'intIp':
      return <Truncate title={d.intIpAddress}><span className="font-mono text-xs">{d.intIpAddress ?? '—'}</span></Truncate>;
    case 'extIp':
      return <Truncate title={d.extIpAddress}><span className="font-mono text-xs">{d.extIpAddress ?? '—'}</span></Truncate>;
    case 'agent':
      return <Truncate><span className="font-mono text-xs">{d.agentVersion ?? '—'}</span></Truncate>;
    case 'warranty':
      return <Truncate><span className="text-xs">{d.warrantyDate ? fmtDate(d.warrantyDate) : '—'}</span></Truncate>;
    case 'manufacturer':
      return <Truncate title={d.hardware?.manufacturer}>{d.hardware?.manufacturer ?? dash}</Truncate>;
    case 'model':
      return <Truncate title={d.hardware?.model}><span className="text-xs">{d.hardware?.model ?? '—'}</span></Truncate>;
    case 'serial':
      return <Truncate title={d.hardware?.serialNumber}><span className="font-mono text-xs">{d.hardware?.serialNumber ?? '—'}</span></Truncate>;
    case 'links':
      return (
        <div className="flex items-center gap-0.5">
          {d.webRemoteUrl && (
            <a href={d.webRemoteUrl} target="_blank" rel="noreferrer" title="Web Remote"
              className="inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-accent-foreground">
              <MonitorPlay className="h-3.5 w-3.5" />
            </a>
          )}
          <a href={d.portalUrl} target="_blank" rel="noreferrer" title="Open in Datto RMM"
            className="inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-accent-foreground">
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>
      );
  }
}

/** "AzureAD\\GeorgeJosifidis" → "GeorgeJosifidis" (full value stays in the tooltip). */
function stripDomain(user: string): string {
  const i = user.lastIndexOf('\\');
  return i >= 0 ? user.slice(i + 1) : user;
}

/** Zero-pads each octet so IPs sort numerically ("10.0.0.9" before "10.0.0.10"). */
function ipSortKey(ip: string | null): string {
  if (!ip) return '';
  return ip.split('.').map((o) => o.padStart(3, '0')).join('.');
}

// ---- Summary strip ----

/** Fleet-wide counts that double as one-click filters. Always computed over ALL devices. */
function SummaryStrip({ items, remote, onApply }: {
  items: RmmDevice[];
  /** null = ScreenConnect not configured (no tile). */
  remote: { ready: boolean; missing: number } | null;
  onApply: (f: Partial<DeviceFilters>) => void;
}) {
  const stats = useMemo(() => ({
    total: items.length,
    online: items.filter((d) => d.online).length,
    stale: items.filter((d) => matchesStatus(d, 'offline30')).length,
    alerts: items.filter((d) => d.openAlerts > 0).length,
    reboot: items.filter((d) => d.rebootRequired).length,
    patchErr: items.filter((d) => d.patchStatus === 'InstallError').length,
    avIssue: items.filter((d) => d.antivirusStatus && d.antivirusStatus !== 'RunningAndUpToDate').length,
  }), [items]);

  const tiles: { label: string; value: number | string; tone?: 'warning' | 'destructive'; apply: Partial<DeviceFilters> }[] = [
    { label: 'Devices',              value: stats.total,    apply: {} },
    { label: 'Online',               value: stats.online,   apply: { status: 'online' } },
    { label: 'Offline 30d+',         value: stats.stale,    apply: { status: 'offline30' }, tone: 'warning' },
    { label: 'Open alerts',          value: stats.alerts,   apply: { hasAlerts: true }, tone: 'warning' },
    { label: 'Needs reboot',         value: stats.reboot,   apply: { rebootRequired: true }, tone: 'warning' },
    { label: 'Patch errors',         value: stats.patchErr, apply: { patch: ['InstallError'] }, tone: 'destructive' },
    { label: 'AV issues',            value: stats.avIssue,  apply: { av: AV_STATUS_OPTIONS.map((o) => o.value).filter((v) => v !== 'RunningAndUpToDate') }, tone: 'destructive' },
    ...(remote ? [{ label: 'No ScreenConnect', value: remote.ready ? remote.missing : '…', apply: { noRemote: true }, tone: 'warning' as const }] : []),
  ];

  return (
    <div className={cn('my-4 grid gap-2', tiles.length > 7 ? 'grid-cols-8' : 'grid-cols-7')}>
      {tiles.map((t) => (
        <button
          key={t.label}
          type="button"
          onClick={() => onApply(t.apply)}
          title={`Show ${t.label.toLowerCase()}`}
          className="rounded-md border bg-card px-3 py-2.5 text-left shadow-sm transition-colors hover:bg-muted/40"
        >
          <div className="truncate text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground">{t.label}</div>
          <div className={cn(
            'mt-1 text-[20px] font-[650] tabular-nums tracking-[-0.02em]',
            typeof t.value === 'number' && t.value > 0 && t.tone === 'warning' && 'text-warning',
            typeof t.value === 'number' && t.value > 0 && t.tone === 'destructive' && 'text-destructive',
          )}>
            {t.value}
          </div>
        </button>
      ))}
    </div>
  );
}

// ---- Filter bar ----

function FilterBar({
  filters, onChange, clientOpts, categoryOpts, unlinkedCount, remoteConfigured,
}: {
  filters: DeviceFilters;
  onChange: (next: DeviceFilters) => void;
  clientOpts: { value: string; label: string }[];
  categoryOpts: { value: string; label: string }[];
  unlinkedCount: number;
  remoteConfigured: boolean;
}) {
  const scoped = useScopeFilter().companyId != null;
  const set = <K extends keyof DeviceFilters>(k: K, v: DeviceFilters[K]) => onChange({ ...filters, [k]: v });
  const isDefault = JSON.stringify(filters) === JSON.stringify(DEFAULT_FILTERS);

  return (
    <div className="flex flex-wrap items-center gap-2">
      <SearchInput
        withIcon
        placeholder="Hostname, user, IP, serial…"
        className="w-56"
        value={filters.search}
        onValueChange={(v) => set('search', v)}
      />
      {!scoped && <StringMultiSelect label="Client" options={clientOpts} selected={filters.clients} onChange={(v) => set('clients', v)} />}
      <StringMultiSelect label="Type" options={categoryOpts} selected={filters.categories} onChange={(v) => set('categories', v)} />
      <select
        value={filters.status}
        onChange={(e) => set('status', e.target.value as StatusFilter)}
        className={cn(
          'h-8 rounded-md border border-input bg-background px-2 text-sm text-foreground',
          filters.status !== 'any' && 'bg-secondary',
        )}
      >
        {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <StringMultiSelect label="Patching" options={PATCH_STATUS_OPTIONS} selected={filters.patch} onChange={(v) => set('patch', v)} />
      <StringMultiSelect label="Antivirus" options={AV_STATUS_OPTIONS} selected={filters.av} onChange={(v) => set('av', v)} />
      <ToggleChip on={filters.rebootRequired} onClick={() => set('rebootRequired', !filters.rebootRequired)}>Reboot required</ToggleChip>
      <ToggleChip on={filters.hasAlerts} onClick={() => set('hasAlerts', !filters.hasAlerts)}>Has alerts</ToggleChip>
      {(remoteConfigured || filters.noRemote) && (
        <ToggleChip on={filters.noRemote} onClick={() => set('noRemote', !filters.noRemote)}
          title="Windows/macOS computers at clients that use ScreenConnect, with no matching ScreenConnect session">
          No ScreenConnect
        </ToggleChip>
      )}
      {unlinkedCount > 0 && (
        <ToggleChip
          on={filters.unlinked}
          onClick={() => set('unlinked', !filters.unlinked)}
          title={`${unlinkedCount} Datto site(s) with devices aren't linked to an Autotask company`}
        >
          Unlinked sites
        </ToggleChip>
      )}
      <Button variant="ghost" size="sm" onClick={() => onChange(DEFAULT_FILTERS)} disabled={isDefault}
        title={isDefault ? 'Filters are at defaults' : 'Reset filters'}>
        <RotateCcw className="mr-1 h-3 w-3" />Reset filters
      </Button>
    </div>
  );
}

function ToggleChip({ on, onClick, title, children }: { on: boolean; onClick: () => void; title?: string; children: ReactNode }) {
  return (
    <Button type="button" size="sm" variant={on ? 'secondary' : 'outline'} onClick={onClick} title={title} aria-pressed={on}>
      {children}
    </Button>
  );
}

// ---- Header + column picker ----

function HeaderCell({
  col, sort, onSort, onResizeStart,
}: {
  col: ColumnDef;
  sort: SortState;
  onSort: (k: SortKey) => void;
  onResizeStart: (k: ColumnKey, e: React.PointerEvent) => void;
}) {
  const active = sort?.key === col.key;
  const Icon = !active ? ArrowUpDown : sort!.dir === 'asc' ? ChevronUp : ChevronDown;
  return (
    <th className="relative select-none px-3 py-2 text-left font-medium">
      {col.unsortable ? (
        <span className="truncate">{col.label}</span>
      ) : (
        <button
          type="button"
          onClick={() => onSort(col.key as SortKey)}
          className={cn('inline-flex max-w-full items-center gap-1 truncate transition-colors', active ? 'text-foreground' : 'hover:text-foreground')}
        >
          <span className="truncate">{col.label}</span>
          <Icon className={cn('h-3 w-3 shrink-0', active ? 'opacity-80' : 'opacity-40')} />
        </button>
      )}
      <div
        onPointerDown={(e) => onResizeStart(col.key, e)}
        className="absolute right-0 top-0 z-10 h-full w-1.5 cursor-col-resize touch-none hover:bg-primary/40 active:bg-primary/60"
      />
    </th>
  );
}

function ColumnPicker({
  visible, onToggle, onReset, onResetWidths,
}: {
  visible: Set<ColumnKey>;
  onToggle: (k: ColumnKey, on: boolean) => void;
  onReset: () => void;
  onResetWidths: () => void;
}) {
  const groups = ['Core', 'Health', 'System', 'Hardware'] as const;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="h-7 gap-1.5 text-xs">
          <Columns3 className="h-3.5 w-3.5" /> Columns
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="max-h-[70vh] w-56 overflow-auto">
        {groups.map((g, i) => (
          <div key={g}>
            {i > 0 && <DropdownMenuSeparator />}
            <DropdownMenuLabel>{g}</DropdownMenuLabel>
            {ALL_COLUMNS.filter((c) => c.group === g).map((c) => (
              <DropdownMenuCheckboxItem
                key={c.key}
                checked={c.required || visible.has(c.key)}
                disabled={c.required}
                onCheckedChange={(v) => onToggle(c.key, !!v)}
                onSelect={(e) => e.preventDefault()}
              >
                {c.label}{c.required && <span className="ml-2 text-xs text-muted-foreground">(required)</span>}
              </DropdownMenuCheckboxItem>
            ))}
          </div>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={onReset}>Reset visibility</DropdownMenuItem>
        <DropdownMenuItem onSelect={onResetWidths}>Reset column widths</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
